import React from "react";
import { Link } from "react-router-dom";

const footerSections = [
  {
    title: "Solutions",
    links: [
      { name: "All Solutions", href: "/solutions" },
      {
        name: "For Streaming Platforms",
        href: "/solutions/streaming-platforms",
      },
      {
        name: "For Online Communities",
        href: "/solutions/online-communities",
      },
      { name: "For Generative AI Apps", href: "/solutions/generative-ai-apps" },
      { name: "For Marketplaces", href: "/solutions/marketplaces" },
      { name: "Static Image API", href: "/solutions/static-image-api" },
    ],
  },
  {
    title: "Products",
    links: [
      { name: "Zenstream", href: "/" },
      { name: "Enterprise", href: "/enterprise/sales" },
      { name: "Face Blurring", href: "/features/face_blurring" },
      { name: "Image Blurring", href: "/features/image_blurring" },
      { name: "Profanity Filter", href: "/features/profanity_filter" },
      {
        name: "Livestream Profanity Filter",
        href: "/features/livestream_profanity_filter",
      },
      { name: "OBS Integration", href: "/features/obs_integration" },
    ],
  },
  {
    title: "Use Cases",
    links: [
      {
        name: "Profanity Filter for YouTube",
        href: "/use-cases/profanity-filter-youtube",
      },
      { name: "Blur Faces in Videos", href: "/use-cases/blur-faces-video" },
      {
        name: "Prevent Demonetization",
        href: "/use-cases/youtube-demonetization-prevention",
      },
      { name: "AI Video Clipping", href: "/use-cases/ai-video-clipping" },
      {
        name: "Podcast to Clips",
        href: "/use-cases/podcast-to-short-clips",
      },
    ],
  },
  {
    title: "Compare",
    links: [
      { name: "All Comparisons", href: "/compare" },
      { name: "vs Opus Clip", href: "/compare/opus-clip" },
      { name: "vs Descript", href: "/compare/descript" },
      { name: "vs Kapwing", href: "/compare/kapwing" },
      { name: "vs VEED", href: "/compare/veed" },
      { name: "vs Submagic", href: "/compare/submagic" },
      { name: "vs Riverside", href: "/compare/riverside" },
    ],
  },
  {
    title: "Resources",
    links: [
      {
        name: "Monetization Database",
        href: "/briefs/youtube_guidelines_database",
      },
      { name: "Affiliate", href: "/affiliate" },
      { name: "Learning Center", href: "/learning-center" },
      { name: "Pricing", href: "/pricing" },
      { name: "Contact Sales", href: "/contact-sales" },
      { name: "General Questions", href: "/contact" },
    ],
  },
  {
    title: "Company",
    links: [
      { name: "Privacy Policy", href: "/legal/privacy_policy" },
      { name: "Terms of Use", href: "/legal/terms_of_use" },
      { name: "European Union GDPR", href: "/legal/european_union_gdpr" },
    ],
  },
];

const isExternal = (href) => href.startsWith("http");

const FooterLink = ({ href, children }) => {
  if (isExternal(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-[15px] text-neutral-400 hover:text-white transition-colors duration-200"
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={href}
      reloadDocument
      className="text-[15px] text-neutral-400 hover:text-white transition-colors duration-200"
    >
      {children}
    </Link>
  );
};

export default function Footer() {
  return (
    <footer className="bg-black text-white mt-20 font-display">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-12 lg:gap-14">
          {/* Left Side */}
          <div className="flex flex-col justify-between">
            <div>
              <Link
                to="/"
                reloadDocument
                className="inline-block text-5xl md:text-6xl font-medium tracking-tight text-white"
              >
                Zenstream
              </Link>

              <p className="mt-4 text-sm text-neutral-400 max-w-xs leading-relaxed">
                AI-powered natural-language video effects.
              </p>

              <div className="mt-8">
                <Link
                  to="/contact-sales"
                  reloadDocument
                  className="inline-flex items-center justify-center min-w-[220px] h-12 px-6 bg-white text-black font-semibold text-sm rounded-full hover:bg-neutral-200 transition-colors duration-150"
                >
                  Contact Sales
                </Link>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white/15">
              <p className="text-base font-medium text-white mb-4">
                Follow us on
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <a
                  href="https://www.linkedin.com/company/zenstreamapp"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border border-white/30 flex items-center justify-center text-sm font-semibold text-white hover:bg-white hover:text-black transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  in
                </a>
                <a
                  href="https://www.instagram.com/zenstreamapp/"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border border-white/30 flex items-center justify-center text-sm font-semibold text-white hover:bg-white hover:text-black transition-colors duration-200"
                  aria-label="Instagram"
                >
                  ig
                </a>
              </div>
            </div>
          </div>

          {/* Right Side Columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-8 gap-y-10">
            {footerSections.map((section) => (
              <div key={section.title}>
                <div className="border-t border-white/15 pt-4">
                  <h3 className="text-[15px] font-semibold text-white mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={link.name}>
                        <FooterLink href={link.href}>{link.name}</FooterLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-6 border-t border-white/15 flex flex-col md:flex-row md:flex-wrap gap-y-4 gap-x-10 text-[15px] text-neutral-400">
          <span>© {new Date().getFullYear()} Zenstream</span>

          <Link
            to="/legal/privacy_policy"
            reloadDocument
            className="hover:text-white transition-colors duration-200"
          >
            Privacy Policy
          </Link>

          <Link
            to="/legal/terms_of_use"
            reloadDocument
            className="hover:text-white transition-colors duration-200"
          >
            Terms of Use
          </Link>

          <Link
            to="/legal/european_union_gdpr"
            reloadDocument
            className="hover:text-white transition-colors duration-200"
          >
            GDPR
          </Link>

          <Link
            to="/affiliate"
            reloadDocument
            className="hover:text-white transition-colors duration-200"
          >
            Affiliate
          </Link>
        </div>
      </div>
    </footer>
  );
}
